import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { AlertCircle, ChevronDown, Loader2, MicOff } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { SheetBody } from '@/components/custom/sheet-layout';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import {
  AudioBar,
  ConversationView,
  type ConversationAudioProps,
} from '@/components/custom/conversation-view';
import { formatDuration } from '@/components/custom/conversation-view/format';
import { ringLeadIn } from '@/components/custom/conversation-view/audio-sync';
import type { InteractionResponse } from '@vagusx/clients';
import { cn } from '@/lib/utils';
import {
  useInteraction,
  useInteractionEvaluation,
  useInteractionMessages,
  useRecordingUrl,
} from '@/hooks/use-interactions';
import { usePatient } from '@/hooks/use-patients';
import { resolveRecordingAnchor } from './recording-anchor';
import {
  BookingClaimMismatchBadge,
  EvaluationClientSummary,
  EvaluationDetail,
  hasBookingClaimMismatch,
  hasClientContent,
  summarizeCategories,
  useHasEvaluationDetail,
} from './CallEvaluationSection';
import { BookingPill } from './BookingPill';
import { CallStatusPill } from './CallStatusPill';
import { CancellationFeePill } from './CancellationFeePill';
import { CallRating } from './CallRating';

/**
 * The speeds the transport offers. Skimming a call you have already heard is
 * the job, so the list leans fast — but it keeps 0.75× because the other real
 * task on this sheet is making out a word the caller mumbled.
 */
const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];

export interface InteractionDetailSheetProps {
  /** Interaction to display. `null` keeps the sheet closed (we render
   *  it once at the consumer site and toggle by id). */
  interactionId: string | null;
  /** Called when the sheet is dismissed (overlay click, Esc, X). */
  onOpenChange: (open: boolean) => void;
}

/**
 * Side sheet for reviewing a past call.
 *
 * ── Reading order, and why it is this one ────────────────────────────────
 * Identity strip → what happened → the recording → the transcript → how well
 * it was handled. Top to bottom that is: who and when, then the outcome, then
 * the thing the reader clicked through to reach, then the detail they may
 * never want.
 *
 * It used to be evaluation card → recording → transcript, which put the ONE
 * fully-expanded block on the page above the player. The card runs to roughly
 * 390px once it has a headline, an outcome line, an office note, four category
 * rows and (for an admin) an internal disclosure — so on a laptop the sheet
 * opened on a wall of prose with the player below the fold, and the reader had
 * to scroll to reach the recording they came for. The split here is the seam
 * `CallEvaluationSection` already exports along:
 *
 *   - {@link EvaluationClientSummary} — headline, outcome, office note,
 *     callback badge — stays ON TOP, always visible. It is what makes the
 *     recording worth playing.
 *   - {@link EvaluationDetail} — the four category checks and the admin-only
 *     scoring — moves to a CLOSED footer disclosure. It is a second read, not
 *     an opening one.
 *
 * That leaves 52px of header + ~148px of outcome + 58px of transport before
 * the transcript: the player lands about a third down the sheet and nothing
 * above it can grow enough to push it off.
 *
 * ── On duplicating the task page ─────────────────────────────────────────
 * `LastCallSection` renders the same summary on the task overview, so a reader
 * who opens the sheet from THERE sees it twice. Kept anyway: this sheet also
 * opens from the referrals list and from Activity, where nothing behind it
 * explains the call. An unexplained recording is every surface's problem; the
 * repeat is one surface's. What is NOT repeated is the scoring detail, which
 * is the taller half.
 *
 * Deliberately scoped: no related-tasks list, no escalation panel, no
 * patient-timeline jump — those lived on the `/activity/$interactionId` page,
 * which we're deprecating because the dense layout wasn't useful in practice.
 *
 * Open/close is fully controlled by the parent: pass an `interactionId`
 * to open, `null` to close. We call `onOpenChange(false)` on dismissal
 * — the parent flips its own state.
 */
export function InteractionDetailSheet({
  interactionId,
  onOpenChange,
}: InteractionDetailSheetProps): React.ReactElement {
  const {
    data: interaction,
    isLoading: interactionLoading,
    isError: interactionError,
    refetch: refetchInteraction,
  } = useInteraction(interactionId ?? undefined);
  const {
    data: messages,
    isLoading: messagesLoading,
    isError: messagesError,
    refetch: refetchMessages,
  } = useInteractionMessages(interactionId ?? undefined);
  // Same query key `CallEvaluationSection` uses, so this resolves from cache
  // rather than costing a second request. Read here because the header's pills
  // and the footer's trigger label are decided at this level.
  const { data: evaluation } = useInteractionEvaluation(interactionId ?? undefined);

  const isLoading = interactionLoading || messagesLoading;
  // The detail query is the load-bearing one — without an interaction
  // there's no recording or transcript section to render. Treat its
  // failure as the page-level error. A messages-only failure is shown
  // in-line on the transcript section so the recording stays usable.
  const hasError = interactionError;
  const hasRecording = !!(interaction?.recordingUrl && interaction?.startedAt);
  // The stored `recordingUrl` is the raw GCS URL — not playable directly
  // (bucket is private). Mint a short-TTL signed URL on demand; the
  // 25-min stale time keeps repeat opens cheap. Only enabled when the
  // recording section will actually render.
  const {
    data: signed,
    isError: recordingUrlError,
    refetch: refetchRecordingUrl,
  } = useRecordingUrl(interactionId ?? undefined, hasRecording);

  // Playback position + transport state live here so the scrubber and the
  // transcript highlight read the same clock.
  const [currentMs, setCurrentMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [scoringOpen, setScoringOpen] = useState(false);
  // Which interaction's ring has already been skipped past, so the seek below
  // happens once per call and never fights a reader who drags into the ring.
  const seededFor = useRef<string | null>(null);

  // Reset between calls — the sheet is rendered once and re-pointed by id,
  // so without this the next call opens mid-scrub at the previous one's
  // position and highlights whatever line happens to sit there. The scoring
  // disclosure resets for the same reason: opened on one call, it would
  // otherwise greet the next call already expanded.
  useEffect(() => {
    setCurrentMs(0);
    setIsPlaying(false);
    setScoringOpen(false);
    // So the next call's ring can be skipped once, below.
    seededFor.current = null;
  }, [interactionId]);

  // Aligned against the RECORDING's first sample, which only
  // `recordingStartedAtMs` knows. No anchor means no highlight and no
  // click-to-seek — never a guess. See ADR-206.
  const anchorIso = interaction ? resolveRecordingAnchor(interaction) : null;
  // The observed SIP pickup (ADR-339) — never `answeredAt`, which is stamped
  // when the runtime POSTs `/start` and so lands mid-ring on an outbound dial.
  const sipAnsweredIso = interaction?.sipAnsweredAt ?? null;

  const durationMs =
    interaction?.recordingDurationSeconds != null
      ? interaction.recordingDurationSeconds * 1000
      : undefined;

  const leadIn = ringLeadIn(anchorIso ?? undefined, sipAnsweredIso, { durationMs });

  // OPEN PLAYBACK PAST THE RING. Press play and you hear the call, not the
  // silent head of the file. The ring is not hidden — the transcript names it,
  // the scrubber shades it, and dragging left still plays it.
  //
  // Seeded ONCE PER CALL, which is why this is not just a different initial
  // `useState`: the interaction has not loaded when the id changes, so the
  // offset is unknown then, and re-applying it on every render would yank the
  // reader back out of the ring the moment they dragged into it. The ref holds
  // the id already seeded; the reset effect above clears it.
  useEffect(() => {
    if (!leadIn || seededFor.current === interactionId) return;
    seededFor.current = interactionId ?? null;
    setCurrentMs(leadIn.durationMs);
  }, [interactionId, leadIn]);

  const audio: ConversationAudioProps | undefined = useMemo(() => {
    if (!signed?.url) return undefined;
    return {
      src: signed.url,
      // Left undefined when the call has no anchor at all, which switches the
      // highlight and click-to-seek off together and leaves a plain player.
      startedAt: anchorIso ?? undefined,
      // Absent on inbound, browser tests, calls nobody answered, and every row
      // predating the column — the ring row and the scrubber shade both switch
      // off together, which is the same bargain `startedAt` makes.
      answeredAt: sipAnsweredIso ?? undefined,
      durationMs,
      currentMs,
      isPlaying,
      onSeek: setCurrentMs,
      onPlayingChange: setIsPlaying,
    };
  }, [signed?.url, anchorIso, sipAnsweredIso, durationMs, currentMs, isPlaying]);

  const retry = (): void => {
    void refetchInteraction();
    void refetchMessages();
  };

  const categorySummary = evaluation ? summarizeCategories(evaluation) : null;
  // NOT `categorySummary`. That counts the CLIENT categories, which a fallback
  // evaluation does not have — while the admin-only grade, safety gates and
  // improvements it may still carry are what `EvaluationDetail` would show.
  const hasScoringDetail = useHasEvaluationDetail(evaluation);
  // The alarm gets its own home rather than riding the disclosure's trigger.
  // `hasBookingClaimMismatch` reads `llmCallbackRecommendation`, which no part
  // of `hasScoringDetail` looks at, so an admin can hit a row that fires the
  // alarm and offers no disclosure to hang it on — and this is the verdict
  // that must not be the thing we drop.
  const bookingClaimMismatch = !!evaluation && hasBookingClaimMismatch(evaluation);

  return (
    <Sheet open={!!interactionId} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl">
        <SheetHeader className="pb-2">
          <CallIdentity interaction={interaction} />
        </SheetHeader>
        {/* Body fills remaining sheet height via flex (`flex-1 min-h-0` over a
            calc() — see ScheduleAppointmentDrawer) and OWNS THE OUTER SCROLL.

            SheetBody's `overflow-y-auto` is load-bearing. This body previously had no
            overflow at all: the evaluation and recording sat at intrinsic
            height and the transcript's `flex-1` absorbed whatever was left, so
            ConversationView's inner scroller was the only scrollable region.
            That silently assumed everything above the transcript is short —
            which is exactly the assumption the reordering above stops relying
            on, but the outer scroll stays as the backstop for a long headline
            or an open scoring footer. */}
        <SheetBody className="flex flex-col gap-3">
          {hasError ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
              <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
              <p>Couldn&apos;t load this call.</p>
              <Button variant="outline" size="sm" onClick={retry}>
                Try again
              </Button>
            </div>
          ) : isLoading || !interaction ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading call…
            </div>
          ) : (
            <>
              {/* Tier 1 of the evaluation, and the only part above the player.
                  Renders nothing when the call has no evaluation — inbound
                  calls never get one, and an outbound call is unevaluated for
                  the first seconds-to-minutes after it ends. The sheet is then
                  header → player → transcript, which is still the right shape.

                  `showStatusPill={false}`: the identity strip above already
                  carries that pill, and the fragment would otherwise put a
                  second identical one beside the headline. */}
              {evaluation && hasClientContent(evaluation) && (
                <section className="shrink-0 space-y-3">
                  <EvaluationClientSummary evaluation={evaluation} showStatusPill={false} />
                </section>
              )}

              {/* Above the player, beside the outcome it contradicts — not on
                  the scoring disclosure's trigger, which only exists when
                  there is scoring to show. Full text rather than `compact`:
                  standing alone it has no neighbouring pill to read against. */}
              {bookingClaimMismatch && (
                <div className="shrink-0">
                  <BookingClaimMismatchBadge />
                </div>
              )}
              {/* The caller's own verdict (ADR-200), under the model's. Outside
                  the block above because it does not depend on an evaluation —
                  an inbound call is never evaluated but can still carry a
                  rating. Renders nothing when there is none, which is most
                  calls. */}
              <CallRating rating={interaction.callRating ?? null} className="shrink-0" />

              {/* The player sits directly under the outcome and above the
                  transcript — the position the whole reordering exists to
                  protect. It is a CONTROLLED bar the transcript reads, not a
                  native element: one `currentMs`, so the highlight and the
                  scrubber cannot disagree. `showAudioBar={false}` below stops
                  ConversationView drawing a second one at the foot of the
                  transcript, where it would sit off screen. */}
              <section className="shrink-0">
                {hasRecording && audio ? (
                  <AudioBar
                    audio={audio}
                    playbackRates={PLAYBACK_RATES}
                    className="rounded-md border"
                  />
                ) : recordingUrlError ? (
                  // The signed-URL query is `retry: false`, so a failure is
                  // FINAL — it will not resolve on its own. An indefinite
                  // spinner here reads as "still working" for ever; the first
                  // cut did exactly that and looked like a hung player. Say
                  // it failed and offer the retry.
                  <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-muted-foreground">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                    <span className="flex-1">Couldn&apos;t load the recording.</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => void refetchRecordingUrl()}
                    >
                      Try again
                    </Button>
                  </div>
                ) : hasRecording ? (
                  <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Preparing the recording…</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    <MicOff className="h-3.5 w-3.5" />
                    <span>No recording was saved for this call.</span>
                  </div>
                )}
              </section>

              <section className="flex min-h-0 flex-1 flex-col">
                {/* Three things are load-bearing here.

                    `flex flex-col`: ConversationView's outer wrapper uses
                    `flex-1`, which only resolves inside a flex parent. Without
                    it the inner scroller can't take a bounded height and the
                    whole transcript blows past the sheet.

                    `flex-1`: the transcript claims whatever vertical space the
                    outcome block and the player leave. This replaces an earlier
                    `h-[45vh]`, which pinned the transcript to a fraction of the
                    viewport and left the bottom third of a tall sheet empty.
                    `flex-1` DOES resolve here despite the scrolling parent: the
                    body's own height is definite (it is `flex-1` under the
                    sheet's `h-full`), so the flex algorithm has real free space
                    to hand out and ConversationView's inner scroller still gets
                    a bounded height.

                    `min-h-64`: the floor that keeps the outer scroll honest. If
                    the blocks above are tall enough to eat the sheet, the
                    transcript stops shrinking at 16rem and the body scrolls,
                    rather than the transcript collapsing toward zero. */}
                <div className="flex min-h-64 flex-1 flex-col overflow-hidden rounded-md border">
                  {messagesError ? (
                    <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
                      <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
                      <span>Couldn&apos;t load the transcript.</span>
                      <Button variant="outline" size="sm" onClick={() => void refetchMessages()}>
                        Try again
                      </Button>
                    </div>
                  ) : (
                    <ConversationView
                      messages={messages ?? []}
                      showToolCalls={true}
                      audio={audio}
                      showAudioBar={false}
                      // A permanent timecode gutter rather than the hover
                      // timestamps every live surface uses. This is a
                      // RECORDING being reviewed: the column states each line's
                      // position in the audio, so it reads against the
                      // transport's own readout and the transcript becomes a
                      // time axis you can navigate by.
                      showTimestamps="always"
                      emptyState="No transcript captured for this call."
                    />
                  )}
                </div>
              </section>

              {/* Tier 2, closed. The trigger names what is behind it AND how it
                  came out — a disclosure that hides a problem must not read as
                  neutral, so `summarizeCategories` puts "1 to review" on the
                  label when a check is flagged. That count is a LABEL concern,
                  so it is allowed to be absent: an admin-only block with no
                  client categories still opens, just without a count.

                  `showBookingClaimAlarm={false}` because the alarm is rendered
                  above the player unconditionally — it must not depend on this
                  disclosure existing, and must not appear twice when it does. */}
              {evaluation && hasScoringDetail && (
                <Collapsible
                  open={scoringOpen}
                  onOpenChange={setScoringOpen}
                  className="shrink-0 rounded-md border bg-muted/20"
                >
                  <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 transition-transform',
                        scoringOpen && 'rotate-180'
                      )}
                    />
                    How this call was scored
                    {categorySummary && (
                      <span className="ml-auto">
                        <Badge variant={categorySummary.attention > 0 ? 'outline' : 'secondary'}>
                          {categorySummary.label}
                        </Badge>
                      </span>
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-3 px-3 pb-3">
                      <EvaluationDetail evaluation={evaluation} showBookingClaimAlarm={false} />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Who, when, how long, and how it went — the header line.
 *
 * Replaces a static "Call detail / Recording and transcript from this call",
 * which spent the most prominent row on the sheet restating the thing the
 * reader had just clicked. Every element here is a fact about THIS call.
 *
 * The patient's name comes from a separate query because the interaction row
 * carries only `patientId`. It is deliberately non-blocking: the title falls
 * back to "Call detail" and fills in when the name lands, so a slow patient
 * fetch never holds up the recording. Same query key as the patient pages, so
 * it is usually already cached.
 */
function CallIdentity({
  interaction,
}: {
  interaction: InteractionResponse | undefined;
}): React.ReactElement {
  const { data: patient } = usePatient(interaction?.patientId ?? undefined);
  const { data: evaluation } = useInteractionEvaluation(interaction?.id);

  const name = patient ? `${patient.firstName} ${patient.lastName}`.trim() : null;
  const started = interaction?.startedAt ? new Date(interaction.startedAt) : null;
  // `direction` is nullable on the response, so an unknown direction must drop
  // out of the line rather than default to one. A ternary on `=== 'inbound'`
  // printed "Outbound" for a call whose direction was never recorded — and this
  // row exists to carry facts, so inventing one is worse than saying less.
  const direction =
    interaction?.direction === 'inbound'
      ? 'Inbound'
      : interaction?.direction === 'outbound'
        ? 'Outbound'
        : null;

  const meta = [
    direction,
    started && Number.isFinite(started.getTime()) ? format(started, 'd MMM yyyy, h:mm a') : null,
    interaction?.durationSeconds != null
      ? formatDuration(interaction.durationSeconds * 1000)
      : null,
  ].filter((part): part is string => !!part);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pr-8">
      <SheetTitle className="text-base">{name ?? 'Call detail'}</SheetTitle>
      {/* `SheetDescription` rather than a plain span: Radix wires it to the
          dialog's `aria-describedby`, and dropping it entirely logs a missing
          description warning. It happens to be the right element anyway — this
          line IS the description of the call now. */}
      <SheetDescription className="text-xs">
        {meta.length > 0 ? meta.join(' · ') : 'Recording and transcript from this call.'}
      </SheetDescription>
      {/* The fee pill is read from the interaction, not the evaluation, so the
          row renders for it even on a call the evaluator never judged (ADR-290). */}
      {(evaluation || interaction?.cancellationFeeCents) && (
        <div className="flex flex-wrap items-center gap-2">
          {evaluation?.status && <CallStatusPill status={evaluation.status} />}
          <CancellationFeePill cancellationFeeCents={interaction?.cancellationFeeCents} />
          {evaluation && (
            <BookingPill
              outcomeCategory={evaluation.outcomeCategory}
              bookedOnThisCall={evaluation.bookedOnThisCall}
            />
          )}
        </div>
      )}
    </div>
  );
}
